import React, { useMemo, useState } from "react";
import { Clock, AlertTriangle, Sparkles, Grid3X3, List, Mic2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { formatTimestamp } from "@/lib/utils";

// Convert moment timestamp to formatted time (HH:MM:SS for > 1 hour)
const getFormattedTimestamp = (moment) => {
  if (moment?.reference_label) {
    return moment.reference_label;
  }
  const seconds = getTimeInSeconds(moment);
  return formatTimestamp(seconds);
};

// Get time in seconds from moment (parsing timestamp string correctly)
const getTimeInSeconds = (moment) => {
  // Parse the timestamp string (more reliable than time_seconds which may be wrong)
  if (moment.timestamp && typeof moment.timestamp === 'string') {
    const parts = moment.timestamp.split(':');
    if (parts.length === 2) {
      const mins = parseInt(parts[0], 10);
      const secs = parseInt(parts[1], 10);
      return mins * 60 + secs;
    } else if (parts.length === 3) {
      const hours = parseInt(parts[0], 10);
      const mins = parseInt(parts[1], 10);
      const secs = parseInt(parts[2], 10);
      return hours * 3600 + mins * 60 + secs;
    }
  }
  
  // Fallback to time_seconds only if timestamp is not available
  if (moment.time_seconds !== undefined && moment.time_seconds !== null) {
    // Check if time_seconds seems wrong (greater than what the timestamp would give)
    if (moment.timestamp) {
      const parts = moment.timestamp.split(':');
      if (parts.length === 2) {
        const expectedSecs = parseInt(parts[0], 10) * 60 + parseInt(parts[1], 10);
        if (Math.abs(moment.time_seconds - expectedSecs) > 60) {
          return expectedSecs; // time_seconds is wrong, use parsed timestamp
        }
      }
    }
    return moment.time_seconds;
  }
  
  return 0;
};

const KeyMomentsList = ({
  moments,
  onMomentClick,
  sidebarMode = false,
  isDocument = false,
  speakerInsights = [],
  speakerProfiles = [],
  language = "es",
}) => {
  const [viewMode, setViewMode] = useState(sidebarMode ? "list" : moments?.length > 6 ? "grid" : "list");
  const [expandedMoment, setExpandedMoment] = useState(null);

  const speakerLookup = useMemo(() => Object.fromEntries(
    (speakerProfiles || []).map((profile) => [
      profile.speaker_id,
      profile.custom_name || profile.suggested_name || profile.label || profile.speaker_id,
    ])
  ), [speakerProfiles]);

  if (!moments || moments.length === 0) {
    return (
      <div className="p-6 text-center text-muted-foreground">
        No key moments detected
      </div>
    );
  }

  const getImportanceIcon = (importance) => {
    switch (importance) {
      case "critical":
        return <AlertTriangle className="w-4 h-4 text-red-500" />;
      case "high":
        return <Sparkles className="w-4 h-4 text-yellow-500" />;
      default:
        return <Clock className="w-4 h-4 text-blue-500" />;
    }
  };

  const getImportanceClass = (importance) => {
    switch (importance) {
      case "critical":
        return "border-red-500/30 bg-red-500/5 hover:bg-red-500/10";
      case "high":
        return "border-yellow-500/30 bg-yellow-500/5 hover:bg-yellow-500/10";
      default:
        return "border-blue-500/30 bg-blue-500/5 hover:bg-blue-500/10";
    }
  };

  const handleClick = (moment) => {
    if (onMomentClick) {
      onMomentClick(moment);
    }
  };

  const getSpeakerName = (speakerId) => speakerLookup[speakerId] || speakerId || (language === "es" ? "Speaker" : "Speaker");

  return (
    <div className="h-full flex flex-col" data-testid="key-moments-list">
      <div className="shrink-0 border-b border-border p-3" data-testid="key-moments-toolbar">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div className="flex gap-1" data-testid="key-moments-view-toggle">
            <Button
              variant={viewMode === "list" ? "secondary" : "ghost"}
              size="sm"
              onClick={() => setViewMode("list")}
              className="h-7 w-7 p-0"
              data-testid="key-moments-list-view-button"
            >
              <List className="w-4 h-4" />
            </Button>
            <Button
              variant={viewMode === "grid" ? "secondary" : "ghost"}
              size="sm"
              onClick={() => setViewMode("grid")}
              className="h-7 w-7 p-0"
              data-testid="key-moments-grid-view-button"
            >
              <Grid3X3 className="w-4 h-4" />
            </Button>
          </div>
        </div>
      </div>

      <div className={`flex-1 overflow-auto p-4 ${viewMode === "grid" ? (sidebarMode ? "grid grid-cols-1 auto-rows-min gap-4" : "grid grid-cols-2 auto-rows-min gap-3") : "space-y-4"}`}>
        {moments.map((moment, idx) => (
          <div
            key={idx}
            onClick={() => handleClick(moment)}
            onMouseEnter={() => setExpandedMoment(idx)}
            onMouseLeave={() => setExpandedMoment(null)}
            onTouchStart={() => setExpandedMoment(expandedMoment === idx ? null : idx)}
            className={`cursor-pointer rounded-xl border p-3 transition-all duration-300 ${getImportanceClass(moment.importance)} ${expandedMoment === idx ? "z-50 scale-105 bg-card shadow-xl ring-2 ring-primary/50" : ""}`}
            style={expandedMoment === idx ? { position: "relative" } : {}}
            data-testid={`key-moment-${idx}`}
          >
            {viewMode === "grid" ? (
              <div className="space-y-2">
                <div className="flex items-center justify-between gap-2">
                  <span className={`timestamp-pill font-semibold ${isDocument ? "max-w-[110px] truncate text-[11px]" : "text-sm"}`} title={getFormattedTimestamp(moment)}>
                    {getFormattedTimestamp(moment)}
                  </span>
                  {getImportanceIcon(moment.importance)}
                </div>
                {moment.speaker_id ? (
                  <div className="flex items-center gap-2 text-xs text-primary" data-testid={`key-moment-speaker-${idx}`}>
                    <Mic2 className="h-3.5 w-3.5" />
                    <span>{getSpeakerName(moment.speaker_id)}</span>
                  </div>
                ) : null}
                <h4 className={`font-medium leading-7 ${sidebarMode ? "text-base" : "text-sm sm:text-base"} ${expandedMoment === idx ? "" : "line-clamp-2"}`}>
                  {moment.title}
                </h4>
                <p className={`${sidebarMode ? "text-base leading-7" : "text-sm leading-7 sm:text-base"} text-muted-foreground ${expandedMoment === idx ? "" : "line-clamp-3"}`}>
                  {moment.description}
                </p>
                {(moment.speaker_quote || (isDocument && moment.excerpt)) ? (
                  <p className={`border-l border-primary/30 pl-3 text-muted-foreground/85 italic ${sidebarMode ? "text-sm leading-6" : "text-xs leading-6 sm:text-sm"} ${expandedMoment === idx ? "" : "line-clamp-3"}`}>
                    “{moment.speaker_quote || moment.excerpt}”
                  </p>
                ) : null}
              </div>
            ) : (
              <div className={`flex items-start ${sidebarMode ? "gap-3" : "gap-2"}`}>
                <span className={`timestamp-pill shrink-0 font-semibold text-sm ${isDocument ? "max-w-[110px] truncate text-[11px]" : ""}`} title={getFormattedTimestamp(moment)}>
                  {getFormattedTimestamp(moment)}
                </span>
                <div className="min-w-0 flex-1">
                  <div className="mb-1 flex items-center gap-1.5">
                    {getImportanceIcon(moment.importance)}
                    <h4 className={`font-medium ${sidebarMode ? "text-base leading-7" : "text-sm leading-7 sm:text-base"}`}>{moment.title}</h4>
                  </div>
                  {moment.speaker_id ? (
                    <div className="mb-2 flex items-center gap-2 text-xs text-primary" data-testid={`key-moment-speaker-${idx}`}>
                      <Mic2 className="h-3.5 w-3.5" />
                      <span>{getSpeakerName(moment.speaker_id)}</span>
                    </div>
                  ) : null}
                  <p className={`${sidebarMode ? "text-base leading-7" : "text-sm leading-7 sm:text-base"} text-muted-foreground ${expandedMoment === idx ? "" : "line-clamp-3"}`}>
                    {moment.description}
                  </p>
                  {(moment.speaker_quote || (isDocument && moment.excerpt)) ? (
                    <p className={`mt-2 border-l border-primary/30 pl-3 text-muted-foreground/85 italic ${sidebarMode ? "text-sm leading-6" : "text-xs leading-6 sm:text-sm"} ${expandedMoment === idx ? "" : "line-clamp-3"}`}>
                      “{moment.speaker_quote || moment.excerpt}”
                    </p>
                  ) : null}
                </div>
              </div>
            )}
          </div>
        ))}
      </div>
    </div>
  );
};

export default KeyMomentsList;
